export interface SoftPromptScheduleState {
  readonly softPromptsShown: number;
  readonly nextSoftPromptAt: number | null;
  readonly previousCompletedCount: number;
}

export interface SoftPromptScheduleInputs {
  readonly interval: number;
  readonly maxInterval: number;
  readonly completedCount: number;
  readonly isLastCompletedSubmitted: boolean;
}

export interface SoftPromptScheduleDecision {
  readonly state: SoftPromptScheduleState;
  readonly showPrompt: boolean;
  readonly clearActivePrompt: boolean;
}

export const INITIAL_SOFT_PROMPT_SCHEDULE_STATE: SoftPromptScheduleState = {
  softPromptsShown: 0,
  nextSoftPromptAt: null,
  previousCompletedCount: 0,
};

/**
 * Pure scheduler for the inline soft feedback prompt. Each call returns the
 * next state plus whether to show the prompt this tick. The back-off doubles
 * the gap after each shown prompt (interval, 2×, 4×, …) and clamps at
 * `maxInterval`. When the completed-message count shrinks (chat reset), the
 * counters and any active prompt are cleared. When the last completed
 * message has already been rated, the scheduler advances by one base
 * interval without consuming a back-off slot.
 */
export function nextSoftPromptDecision(
  previous: SoftPromptScheduleState,
  inputs: SoftPromptScheduleInputs,
): SoftPromptScheduleDecision {
  let softPromptsShown = previous.softPromptsShown;
  let nextSoftPromptAt = previous.nextSoftPromptAt;
  let clearActivePrompt = false;

  if (inputs.completedCount < previous.previousCompletedCount) {
    softPromptsShown = 0;
    nextSoftPromptAt = null;
    clearActivePrompt = true;
  }

  const baseState: SoftPromptScheduleState = {
    softPromptsShown,
    nextSoftPromptAt,
    previousCompletedCount: inputs.completedCount,
  };

  if (inputs.completedCount === 0) {
    return { state: baseState, showPrompt: false, clearActivePrompt };
  }

  const threshold = nextSoftPromptAt ?? inputs.interval;

  if (inputs.completedCount < threshold) {
    return {
      state: { ...baseState, nextSoftPromptAt: threshold },
      showPrompt: false,
      clearActivePrompt,
    };
  }

  if (inputs.isLastCompletedSubmitted) {
    return {
      state: {
        ...baseState,
        nextSoftPromptAt: inputs.completedCount + inputs.interval,
      },
      showPrompt: false,
      clearActivePrompt,
    };
  }

  const newShownCount = softPromptsShown + 1;
  const gap = Math.min(
    inputs.interval * 2 ** newShownCount,
    inputs.maxInterval,
  );

  return {
    state: {
      ...baseState,
      softPromptsShown: newShownCount,
      nextSoftPromptAt: inputs.completedCount + gap,
    },
    showPrompt: true,
    clearActivePrompt,
  };
}
